import classnames from 'classnames'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Abstract, Description, Title } from './components/Typography'
import config from '../../../config'
import { AvatarImage } from '../../common/AvatarImage'
import { mediaMax } from '../../constants/breakpoints'
import { Event } from '../../types/viewModels'
import { ButtonStar } from '../Card/ButtonStar'

// TODO: Handle no image.  We have no defualt image.
interface SpeakerImagesProps {
  image: string
}
const SpeakerImage: React.FC<SpeakerImagesProps> = ({
  image,
}) => {
  if(!!image) {
    return (
      <StyledAvatarImage
        key={image}
        image={`${config.LISA_IMAGE_BASE}${image}`}
        imageFallback={config.FALLBACK_PERSON_IMAGE}
        alt="speaker image"
        height={680}
        width={680}
      />
      
    )
  }
  return (
    <StyledAvatarImage
      imageFallback={config.FALLBACK_PERSON_IMAGE}
      alt="speaker image"
      height={680}
      width={680}
      priority
    />
  )
}

export interface EventProps {
  event: Event
  className?: string
  style?: React.CSSProperties
  type: 'fullpage' | 'preview'
  onFavorites?: (id: string, title: string) => void
  favoriteEvents?: string[]
  favoritesLoading: boolean
}


const EventInfo: React.FC<EventProps> = ({
  event,
  className,
  style,
  type,
  onFavorites,
  favoriteEvents,
  favoritesLoading,
}: EventProps) => {
  const { t } = useTranslation('common')
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  
  const cardIsFavorite = favoriteEvents && !!favoriteEvents.filter((x) => x === event.id)[0]
  
  const e = event
  const handleFavorites = (event: React.MouseEvent) => {
    event.preventDefault()
    onFavorites && onFavorites(e.id, e.titleSimple)
  }

  return (
    <EventWrapper id="vidburdur-info-event" className={`${className} ${type}`} style={style}>
      <Content className="event-content">
        <StyledTitle dangerouslySetInnerHTML={{
          __html: event.title !== undefined ? event.title : '',
        }}
        />

        <PointInfo>
          <PointInfoText>{event.location.displayName}</PointInfoText>
          <Point />
          <PointInfoText>{`${event.timeInterval}`}</PointInfoText>
        </PointInfo>

        <SpeakerContainer>
          {event.speakers.map(speaker => {
            return(
              <Speaker key={speaker.name}>
                <SpeakerImage image={speaker.image ? speaker.image[0] : undefined}/>
                <SpeakerInfo>
                  <SpeakerName>{speaker.name}</SpeakerName>
                  <SpeakerJob>{speaker.company}</SpeakerJob>
                </SpeakerInfo>
              </Speaker>
            )
          })}
        </SpeakerContainer>

        <EventText>
          {favoriteEvents != undefined ? (
            <ButtonContainer>
              <ButtonStar
                className="action-button"
                style={favoritesLoading ? { opacity: 0, pointerEvents: 'none' } : { justifyContent: 'flex-start'}}
                active={cardIsFavorite}
                title={cardIsFavorite ? (t('RemoveFromFavorite') ) : t('AddToFavorite')}
                onClick={handleFavorites}
              />
            </ButtonContainer>
          ) : ('')}

          <Abstract dangerouslySetInnerHTML={{
            __html: event.abstract !== undefined ? event.abstract : '',
          }}
          />

          {type === 'fullpage' && (
            <Description dangerouslySetInnerHTML={{
              __html: event.information !== undefined ? event.information : '',
            }}
            />
          )}

          {(type === 'preview' && !!event.information) && (
            <>
              <AccordionHeader
                initial={false}
                onClick={() => setDescriptionOpen(!descriptionOpen)}
                className={descriptionOpen ? 'open' : ''}
              />
              <AnimatePresence initial={false}>
                {descriptionOpen && (
                  <motion.section
                    key="content"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { opacity: 1, height: 'auto' },
                      collapsed: { opacity: 0, height: 0 },
                    }}
                    transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
                  >
                    <Description dangerouslySetInnerHTML={{
                      __html: event.information !== undefined ? event.information : '',
                    }}
                    />
                  </motion.section>
                )}
              </AnimatePresence>
            </>
          )}
        </EventText>
      </Content>
    </EventWrapper>
  )
}

export default EventInfo

const PointInfoText = styled.div`
  a {
    text-decoration: underline;
  }
`

const Point = styled.div`
  height: 100%;
  display: flex;
  margin: 0 1em;

  &:before {
    content: '';
    height: 0.29em;
    width: 0.29em;
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: 50%;
  }
`

const PointInfo = styled.div`
  font-size: max(14px, 1vw);
  font-weight: normal;
  line-height: 170%;
  font-family: ${({ theme }) => theme.fonts.fontFamilySecondary};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  padding: 1.25em 0;
  margin-bottom: 2em;

  &:before, &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.colors.white};
  }

  &:before {
    top: 0;
    left: 0;
  }
  &:after {
    bottom: 0;
    left: 0;
  }
`

const ButtonContainer = styled.div`
  padding-bottom: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
`

const ButtonText = styled.p`
  font-size: max(0.97vw, 14px);
  font-family: Open Sans;
  color: white;
`

const AccordionHeader = styled(motion.header)`
  background-image:url('/icon.png');
  background-repeat: no-repeat;
  background-color:transparent !important;
  cursor: pointer;
  transform: rotateX(0deg);
  transition: transform 0.2s linear;
  transform-origin: center;
  height: max(1.4vw, 20px);
  width: max(1.4vw, 20px);
  margin-top: 20px;

  &.open{
    transform: rotateX(180deg);
    transition: transform 0.2s linear;
    margin-bottom: 20px;
  }
`

const StyledDate = styled.div`
  font-size: max(1.4vw, 20px);
  line-height: 25px;
  color: ${({ theme }) => theme.colors.third.base};
  margin-bottom: 15px;
  @media ${mediaMax.mobileL} {
    width: 70%;
  }
`

const EventText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  @media ${mediaMax.mobileL} {
    width: 100%;
    min-height: unset;
    overflow: unset;
    padding: 0 0 0 0;
    h1 {
      text-align: left;
    }
  }
`

const SpeakerName = styled.h1`
  color: ${({ theme }) => theme.colors.white};
  font-size: max(15px, 1.04vw);
  line-height: 140%;
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.fontFamilyPrimary};
  margin-bottom: 0.5em;
  white-space: nowrap;

  @media ${mediaMax.tablet}{
    white-space: normal;
  }
`

const SpeakerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: max(25px, 2.7vw);
`

const Speaker = styled.div`
  display: flex;
  flex-direction: row;
  align-items:center;
  justify-content: flex-start;
`

const StyledAvatarImage = styled(AvatarImage)`
  background-position: center;
  background-size: cover;
  width: max(5.3vw, 76px);
  height: max(5.3vw, 76px);
`

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
  font-size: max(24px, 1.67vw);
  margin-left: 25px;

  @media ${mediaMax.tablet} {
    margin-bottom: 0;
    margin-left: 25px;
    align-items: flex-start;
  }
`

const SpeakerJob = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.white};
  font-size: max(0.97vw, 14px);
  font-family: ${({ theme }) => theme.fonts.fontFamilySecondary};
`

const EventWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;

  &.fullpage{
    padding: 18vh 0 18vh 0;
    ${Description}{
      margin-top:15px;
    }
  }
`

const Content = styled.div`
  padding-left: calc(9.2vw);
  width: 53.8889vw;
  display: flex;
  flex-direction: column;
  position: relative;
  font-family: ${({ theme }) => theme.fonts.fontFamilyPrimary};
  @media ${mediaMax.tablet} {
    justify-content: flex-start;
    flex-direction: column;
    width: 100%;
    padding: 0px 15px 0px 15px;
  }
`

const StyledTitle = styled(Title)`
  width: 100%;
  padding-bottom: max(2.57vw, 20px);
`

const NameList = styled.li`
  list-style-type: none;
  color: #fff;
  font-family: Open Sans;
  font-size: max(17px,1.2vw);
  font-weight: normal;
  margin: 2px 0;
`
